import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {DatePipe} from '@angular/common';
import {
    isOrganisationActive,
    OrganisationCareProgram,
    OrganisationDetail
} from '@shared/api/models/query/organisation.model';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {Permissions} from '@models/permissions.enum';
import {PermissionRolesMap} from '@models/permissions.db';
import {AuthService} from '@shared/services/security/auth.service';
import {ConfirmDialogDirective} from '@shared/components/confirm-dialog/confirm-dialog.directive';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';
import {LinkedRecordsTabBase} from '@shared/components/linked-records/linked-records-tab.base';
import {LinkRecordsModalConfig} from '@shared/components/linked-records/link-records.modal';

/**
 * UNIVAPLAT-3696: Care programmes tab of the organisation detail page.
 *
 * - Lists the care programmes linked to the organisation, newest first. Link and unlink need
 *   MANAGE_ORGANISATION_CARE_PROGRAMS (general admins and the organisation's admins) and an active
 *   organisation; they change what a clinician sees (hazard H039).
 * - The API answers 422 on the linked lists of a deactivated or deleted organisation, so for those
 *   the tab shows a message and neither loads nor calls `linked=false`.
 * - The link popup lists the catalogue entries not yet linked (`linked=false`) and adds the
 *   selection with one `addCareProgrammes` call. Every link and unlink is logged by the API.
 * - Backend owns the error text. Programme data is never logged and is dropped on destroy.
 */
@Component({
    selector: 'cdss-organisation-care-programs-tab',
    standalone: true,
    imports: [DatePipe, ConfirmDialogDirective, SpinnerComponent],
    templateUrl: './organisation-care-programs-tab.component.html',
    styleUrl: './organisation-care-programs-tab.component.scss'
})
export class OrganisationCareProgramsTabComponent extends LinkedRecordsTabBase<OrganisationCareProgram> implements OnChanges {
    @Input() organisation!: OrganisationDetail;

    protected readonly entityLabel = 'Care programme';

    private readonly organisationsService = inject(OrganisationsService);
    private readonly authService = inject(AuthService);

    get isActive(): boolean {
        return !!this.organisation && isOrganisationActive(this.organisation);
    }

    get canWrite(): boolean {
        return this.isActive && this.hasPermission(Permissions.MANAGE_ORGANISATION_CARE_PROGRAMS);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['organisation']) {
            return;
        }
        const previous = changes['organisation'].previousValue as OrganisationDetail | undefined;
        const current = this.organisation;
        // Reload on first render, a different organisation, or an active/inactive flip.
        if (!previous || previous.id !== current?.id || isOrganisationActive(previous) !== isOrganisationActive(current)) {
            void this.load();
        }
    }

    rowId(row: OrganisationCareProgram): number {
        return row.id;
    }

    protected async fetch(): Promise<OrganisationCareProgram[]> {
        if (!this.organisation || !this.isActive) {
            return [];
        }
        const rows = await this.organisationsService.listCareProgrammes(this.organisation.id);
        // Newest first; a missing date sorts last.
        return [...rows].sort((a, b) => Date.parse(b.addedOn ?? '') - Date.parse(a.addedOn ?? '') || 0);
    }

    protected async unlinkOne(row: OrganisationCareProgram): Promise<void> {
        await this.organisationsService.removeCareProgramme(this.organisation.id, row.id);
    }

    protected linkModalConfig(): LinkRecordsModalConfig<OrganisationCareProgram> {
        const organisationId = this.organisation.id;
        return {
            heading: 'Link care programmes',
            titleId: 'link-organisation-care-programs-title',
            columns: [
                {header: 'Title', value: programme => programme.title},
                {header: 'Scope', value: programme => programme.scope},
                {header: 'Target', value: programme => programme.target},
                {header: 'Duration (weeks)', value: programme => programme.durationInWeeks},
                {header: 'Modules', value: programme => programme.numberOfModules},
            ],
            loader: () => this.organisationsService.listCareProgrammes(organisationId, {linked: false}),
            submitLabel: 'Add selected to organisation',
            successMessage: 'Care programmes linked to organisation',
            emptyMessage: 'No care programmes left to link',
            submit: async (ids: number[]) => {
                await this.organisationsService.addCareProgrammes(organisationId, ids);
            },
        };
    }

    private hasPermission(permission: Permissions): boolean {
        const userRoles = this.authService.roles ?? [];
        return (PermissionRolesMap[permission] ?? []).some(role => userRoles.includes(role));
    }
}
